import React, { useRef, useState, useEffect, useMemo } from 'react';
import DocViewer, { DocViewerRenderers, IDocument } from '@cyntler/react-doc-viewer';
import PdfViewer from './PdfViewer';
import DocxViewer from './DocxViewer';
import ImageViewer from './ImageViewer';
import UnsupportedViewer from './UnsupportedViewer';
import { ViewerContainerContext } from './ViewerContainerContext';
import type { ViewMode } from './ViewerToolbar';

const pluginRenderers = [PdfViewer, DocxViewer, ImageViewer, UnsupportedViewer, ...DocViewerRenderers];

interface DocumentViewerProps {
  documents: IDocument[];
  style?: React.CSSProperties;
  initialViewMode?: ViewMode;
  toolbarExtra?: React.ReactNode;
}

const DocumentViewer: React.FC<DocumentViewerProps> = React.memo(({ documents, style, initialViewMode, toolbarExtra }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dimensionCacheRef = useRef(new Map<string, { width: number; height: number }>());
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const resizeTimer = useRef<NodeJS.Timeout>();
  const hasInitialSize = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        const h = entry.contentRect.height;

        // First measurement: apply immediately
        if (!hasInitialSize.current) {
          hasInitialSize.current = true;
          setSize({ width: w, height: h });
          return;
        }

        // Subsequent: debounce 150ms after last resize event
        if (resizeTimer.current) clearTimeout(resizeTimer.current);
        resizeTimer.current = setTimeout(() => {
          setSize(prev => (prev && prev.width === w && prev.height === h) ? prev : { width: w, height: h });
        }, 150);
      }
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
    };
  }, []);

  const contextValue = useMemo(() =>
    size ? { ...size, initialViewMode, toolbarExtra, dimensionCache: dimensionCacheRef.current } : null,
    [size, initialViewMode, toolbarExtra]
  );

  return (
    <div ref={containerRef} style={{ overflow: 'hidden', ...style }}>
      {contextValue && (
        <ViewerContainerContext.Provider value={contextValue}>
          <DocViewer
            documents={documents}
            pluginRenderers={pluginRenderers}
            config={{ header: { disableHeader: true } }}
            style={{ height: '100%' }}
          />
        </ViewerContainerContext.Provider>
      )}
    </div>
  );
});

export default DocumentViewer;
